import type { ReactElement } from 'react';
import { presetToDateRange } from '../DashboardPage/dashboardFilterHelpers';

/** Same presets the dashboards and the Opportunities board offer, minus custom ranges. */
export const DATE_RANGE_PRESETS: Array<{ value: string; label: string }> = [
  { value: 'all_time', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'past_7_days', label: 'Past 7 days' },
  { value: 'past_30_days', label: 'Past 30 days' },
  { value: 'past_90_days', label: 'Past 90 days' },
  { value: 'past_12_months', label: 'Last 12 months' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'this_year', label: 'This year' },
];

export const ALL_TIME_PRESET = 'all_time';
export const PAST_7_DAYS_PRESET = 'past_7_days';
export const PAST_30_DAYS_PRESET = 'past_30_days';

/**
 * Converts a preset to a half-open [start, endExclusive) range for filtering
 * timestamp columns: `lte` against a bare end date would cut the range at
 * midnight and drop everything that happened later that day.
 */
export function presetToFilterRange(preset: string): { start: string | null; endExclusive: string | null } {
  const { start, end } = presetToDateRange(preset);
  if (!end) return { start, endExclusive: null };
  const endDate = new Date(`${end}T00:00:00Z`);
  endDate.setUTCDate(endDate.getUTCDate() + 1);
  return { start, endExclusive: endDate.toISOString().slice(0, 10) };
}

interface DateRangeSelectProps {
  value: string;
  onChange: (preset: string) => void;
  className?: string;
  ariaLabel?: string;
}

export function DateRangeSelect({ value, onChange, className, ariaLabel = 'Date range' }: DateRangeSelectProps): ReactElement {
  return (
    <select className={className} aria-label={ariaLabel} value={value} onChange={(event) => onChange(event.target.value)}>
      {DATE_RANGE_PRESETS.map((preset) => <option key={preset.value} value={preset.value}>{preset.label}</option>)}
    </select>
  );
}
